import React, { useState, useEffect } from 'react';
import { absencesApi, type VacationPolicy } from '../../lib/api/absences.api';
import { employeesApi, type Employee } from '../../lib/api/employees.api';
import Button from '../../components/common/Button';

const BatchInitializeVacationBalancesModal: React.FC<{
    isOpen: boolean;
    onClose: () => void;
    onInitialized: () => void;
}> = ({ isOpen, onClose, onInitialized }) => {
    const [loading, setLoading] = useState(false);
    const [year, setYear] = useState(new Date().getFullYear());
    const [policyId, setPolicyId] = useState<string>('');
    const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<Set<string>>(new Set());
    const [force, setForce] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [policies, setPolicies] = useState<VacationPolicy[]>([]);
    const [employees, setEmployees] = useState<Employee[]>([]);
    const [selectAll, setSelectAll] = useState(false);

    useEffect(() => {
        if (isOpen) {
            loadPolicies();
            loadEmployees();
        }
    }, [isOpen]);

    const loadPolicies = async () => {
        try {
            const response = await absencesApi.getVacationPolicies();
            setPolicies(response.filter((p: VacationPolicy) => p.active));
        } catch (err) {
            console.error('Error al cargar políticas:', err);
        }
    };

    const loadEmployees = async () => {
        try {
            const response = await employeesApi.getEmployees();
            setEmployees((response.employees as Employee[]).filter((e: any) => e.active));
        } catch (err) {
            console.error('Error al cargar empleados:', err);
        }
    };

    const handleInitialize = async () => {
        setError(null);
        setSuccess(null);

        try {
            setLoading(true);
            const data = {
                year,
                employeeIds: selectedEmployeeIds.size > 0 ? Array.from(selectedEmployeeIds) : undefined,
                policyId: policyId || undefined,
                force,
            };

            const result = await absencesApi.batchInitializeVacationBalances(data);

            setSuccess(`Inicializados ${result.data.initialized} balances, omitidos ${result.data.skipped} balances`);
            onInitialized();
            
            setTimeout(() => {
                onClose();
                setSuccess(null);
            }, 3000);
        } catch (err: any) {
            setError(err.response?.data?.error || err.message || 'Error al inicializar balances');
        } finally {
            setLoading(false);
        }
    };

    const handleSelectAll = () => {
        const newSelectAll = !selectAll;
        setSelectAll(newSelectAll);
        
        if (newSelectAll) {
            setSelectedEmployeeIds(new Set(employees.map(e => e.id)));
        } else {
            setSelectedEmployeeIds(new Set());
        }
    };

    const handleEmployeeSelect = (employeeId: string) => {
        const newSelected = new Set(selectedEmployeeIds);
        if (newSelected.has(employeeId)) {
            newSelected.delete(employeeId);
        } else {
            newSelected.add(employeeId);
        }
        setSelectedEmployeeIds(newSelected);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                        Inicializar Balances de Vacaciones en Masa
                    </h2>
                </div>

                <div className="p-6 space-y-6">
                    {/* Año */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Año
                        </label>
                        <select
                            value={year}
                            onChange={(e) => setYear(parseInt(e.target.value))}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                        >
                            {[2023, 2024, 2025, 2026, 2027].map(y => (
                                <option key={y} value={y}>{y}</option>
                            ))}
                        </select>
                    </div>

                    {/* Política */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Política de Vacaciones (opcional)
                        </label>
                        <select
                            value={policyId}
                            onChange={(e) => setPolicyId(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                        >
                            <option value="">Usar política activa</option>
                            {policies.map(policy => (
                                <option key={policy.id} value={policy.id}>
                                    {policy.name} ({policy.yearlyDays} días base)
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Empleados */}
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                Empleados (opcional - si no se selecciona, se inicializan todos)
                            </label>
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    id="selectAll"
                                    checked={selectAll}
                                    onChange={handleSelectAll}
                                    className="rounded border-gray-300 dark:border-gray-600 text-primary-600 dark:text-primary-400 focus:ring-primary-500 dark:focus:ring-primary-400"
                                />
                                <label htmlFor="selectAll" className="ml-2 text-sm text-gray-600 dark:text-gray-400">
                                    Seleccionar todos ({employees.length})
                                </label>
                            </div>
                        </div>
                        <div className="border border-gray-200 dark:border-gray-700 rounded-md max-h-40 overflow-y-auto">
                            {employees.map(employee => (
                                <div
                                    key={employee.id}
                                    className="flex items-center px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700"
                                >
                                    <input
                                        type="checkbox"
                                        checked={selectedEmployeeIds.has(employee.id)}
                                        onChange={() => handleEmployeeSelect(employee.id)}
                                        className="rounded border-gray-300 dark:border-gray-600 text-primary-600 dark:text-primary-400 focus:ring-primary-500 dark:focus:ring-primary-400"
                                    />
                                    <span className="ml-3 text-sm text-gray-700 dark:text-gray-300">
                                        {employee.name} {employee.surname} ({employee.dni})
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Forzar */}
                    <div className="flex items-center">
                        <input
                            type="checkbox"
                            id="force"
                            checked={force}
                            onChange={(e) => setForce(e.target.checked)}
                            className="rounded border-gray-300 dark:border-gray-600 text-primary-600 dark:text-primary-400 focus:ring-primary-500 dark:focus:ring-primary-400"
                        />
                        <label htmlFor="force" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                            Forzar reinicialización (sobrescribir balances existentes)
                        </label>
                    </div>

                    {/* Alertas */}
                    {error && (
                        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                            {error}
                        </div>
                    )}
                    {success && (
                        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
                            {success}
                        </div>
                    )}

                    {/* Botones */}
                    <div className="flex justify-end space-x-3">
                        <Button
                            variant="secondary"
                            onClick={onClose}
                            disabled={loading}
                        >
                            Cancelar
                        </Button>
                        <Button
                            onClick={handleInitialize}
                            disabled={loading}
                            loading={loading}
                        >
                            Inicializar Balances
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BatchInitializeVacationBalancesModal;
